// project import
import { lazy } from "react";
import { Navigate, RouteObject } from "react-router-dom";

import Loadable from "@/components/Loadable";
import MainLayout from "@/layouts/MainLayout";

import AdminRoute from "./AdminRoute";
import PrivateRoute from "./PrivateRoute";

const Conversations = Loadable(lazy(() => import("@/pages/conversations")));
const Journeys = Loadable(lazy(() => import("@/pages/journeys")));
const Indexing = Loadable(lazy(() => import("@/pages/indexing")));
const BrandVisibility = Loadable(lazy(() => import("@/pages/brand-visibility")));
const Agents = Loadable(lazy(() => import("@/pages/agents")));
const AgentsList = Loadable(lazy(() => import("@/pages/agents/AgentsList")));
const PoliciesPage = Loadable(lazy(() => import("@/pages/agents/policies")));
const Documentation = Loadable(lazy(() => import("@/pages/documentation")));
const AccountSettings = Loadable(lazy(() => import("@/pages/account-settings")));
const AccountTabPersonal = Loadable(
  lazy(() => import("@/pages/account-settings/tabs/AccountTabPersonal"))
);
const AccountTabEmail = Loadable(
  lazy(() => import("@/pages/account-settings/tabs/AccountTabEmail"))
);
const AccountTabPassword = Loadable(
  lazy(() => import("@/pages/account-settings/tabs/AccountTabPassword"))
);
const AccountTabSettings = Loadable(
  lazy(() => import("@/pages/account-settings/tabs/AccountTabSettings"))
);
const Admin = Loadable(lazy(() => import("@/pages/admin")));
const AdminTabUsers = Loadable(lazy(() => import("@/pages/admin/tabs/users")));
const AdminTabUserAgents = Loadable(lazy(() => import("@/pages/admin/tabs/user-agents")));
const AdminTabSubscribers = Loadable(lazy(() => import("@/pages/admin/tabs/subscribers")));
const AdminTabTrafficCaps = Loadable(lazy(() => import("@/pages/admin/tabs/traffic-caps")));

// ==============================|| MAIN ROUTING ||============================== //

const MainRoutes: RouteObject = {
  path: "/",
  element: (
    <PrivateRoute>
      <MainLayout />
    </PrivateRoute>
  ),
  children: [
    {
      path: "journeys",
      element: (
        <AdminRoute>
          <Journeys />
        </AdminRoute>
      ),
    },
    {
      // Landing → canonical /conversations URL so the sidebar item highlights
      // (the nav active-state matches on pathname, not on which element rendered).
      path: "/",
      element: <Navigate to="/conversations" replace />,
    },
    {
      // BigQuery-backed Conversations dashboard (replaces the legacy Firestore
      // Agent Traffic page + the analytics PoC). Customer-facing — any authenticated
      // user sees their own data; the cross-tenant userId override is admin-only
      // (in the page UI and enforced server-side).
      path: "conversations",
      element: <Conversations />,
    },
    {
      // Kept so existing links / bookmarks to the old dashboard still resolve.
      path: "agent-traffic",
      element: <Conversations />,
    },
    {
      // The crawler/indexer half of Agent Traffic: every bot EXCEPT the live
      // AI assistants Conversations reports on (and except browsers).
      path: "indexing",
      element: <Indexing />,
    },
    {
      path: "brand-visibility",
      element: <BrandVisibility />,
    },
    {
      path: "agents",
      element: <Agents />,
      children: [
        {
          index: true, // This represents the /agents route
          element: <AgentsList />,
        },
        {
          path: "policies/:id",
          element: <PoliciesPage />,
        },
      ],
    },
    {
      path: "documentation",
      element: <Documentation />,
    },
    {
      path: "account",
      children: [
        {
          path: "settings",
          element: <AccountSettings />,
          children: [
            {
              path: "personal",
              element: <AccountTabPersonal />,
            },
            {
              path: "email",
              element: <AccountTabEmail />,
            },
            {
              path: "password",
              element: <AccountTabPassword />,
            },
            {
              path: "settings",
              element: <AccountTabSettings />,
            },
          ],
        },
      ],
    },
    {
      path: "admin",
      element: (
        <AdminRoute>
          <Admin />
        </AdminRoute>
      ),
      children: [
        {
          path: "accounts",
          element: <AdminTabUsers />,
        },
        {
          path: "user-agents",
          element: <AdminTabUserAgents />,
        },
        {
          path: "subscribers",
          element: <AdminTabSubscribers />,
        },
        {
          path: "traffic-caps",
          element: <AdminTabTrafficCaps />,
        },
      ],
    },
  ],
};

export default MainRoutes;
